/**
 * Nexora presentation layer.
 *
 * The Pacxa ERP's visual identity predates the Fluent rebuild: cards with a 10px
 * corner and a soft two-layer shadow, uppercase micro-labels over oversized
 * numbers, 12%-tint status chips, whisper-quiet table chrome. It used to live in
 * `apps/pacxa-assets/src/nexora-ui.css` as a Tailwind `@layer components` block
 * (`.nx-card`, `.nx-kpi`, `.nx-chip`, …) vendored byte-identically into every
 * legacy module app.
 *
 * This is that layer, ported to Griffel and to Fluent's token system so it
 * composes with Fluent v9 instead of fighting it. Two rules kept it honest:
 *
 *  1. **Fluent components stay Fluent components.** These classes are applied to
 *     `Card`, `Table`, `Badge`, `Button` and friends — they restyle, they never
 *     replace. Every keyboard behaviour, ARIA role and focus indicator Fluent
 *     ships is still there.
 *  2. **Colour comes from tokens, never hex.** The only literal colours are the
 *     shadow `rgba()`s, which have no token equivalent — and even those are
 *     mostly delegated to the theme's `shadow*` tokens (see `theme.ts`) so light
 *     and dark can differ.
 *
 * The general shape/elevation traits (10px radius, the dual shadow, the Segoe UI
 * Variable stack) are *not* here: they are theme token overrides in `theme.ts`,
 * so they land on every Fluent surface automatically. What is here is the part
 * Fluent has no opinion about.
 */
import { makeStyles, tokens } from '@fluentui/react-components';
/**
 * Reusable Nexora classes.
 *
 * Compose them with `mergeClasses`, e.g.
 * `mergeClasses(nx.chip, tone === 'over' && nx.chipBad)`.
 */
export const useNexoraStyles = makeStyles({
    /* ── Page heading ─────────────────────────────────────────────────────── */
    /** `.nx-view-h` — title block on the left, actions on the right. */
    pageHeader: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: tokens.spacingHorizontalL,
        marginBottom: tokens.spacingVerticalXL,
    },
    /** `.nx-h1` — 1.5rem bold with the slight negative tracking. */
    pageTitle: {
        display: 'block',
        margin: 0,
        fontSize: '1.5rem',
        fontWeight: tokens.fontWeightBold,
        lineHeight: tokens.lineHeightBase600,
        letterSpacing: '-0.01em',
        color: tokens.colorNeutralForeground1,
    },
    /** `.nx-sub` — the muted one-liner under the title. */
    pageSubtitle: {
        display: 'block',
        marginTop: tokens.spacingVerticalXXS,
        fontSize: '0.82rem',
        lineHeight: tokens.lineHeightBase300,
        color: tokens.colorNeutralForeground2,
    },
    /** `.nx-toolbar` / `.nx-actions` — a wrapping row of controls. */
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: tokens.spacingHorizontalS,
    },
    /* ── Cards ────────────────────────────────────────────────────────────── */
    /**
     * `.nx-card`. The 10px corner and the dual shadow come from
     * `borderRadiusMedium`/`shadow4`, so a plain Fluent `Card` is already most of
     * the way there; this adds the softer border (Nexora draws it at 70% of the
     * border token, which `colorNeutralStroke2` approximates) and hands the
     * padding over to `cardHeader`/`cardBody`.
     *
     * Because the padding and the flex gap Fluent's `Card` supplies are both
     * cleared, a card wearing this class is expected to put its content inside
     * `cardBody` — that is where the Nexora inset comes from.
     */
    card: {
        boxSizing: 'border-box',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusMedium,
        border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
        boxShadow: tokens.shadow4,
        padding: 0,
        rowGap: 0,
    },
    /**
     * `.nx-card-i` — the lift on hover. The transform is dropped under
     * `prefers-reduced-motion`; the border and shadow change still signal the
     * hover, so nothing is lost for people who asked for less movement.
     */
    cardInteractive: {
        transitionProperty: 'border-color, transform, box-shadow',
        transitionDuration: tokens.durationNormal,
        transitionTimingFunction: tokens.curveEasyEase,
        // Griffel does not expand the `border-color` shorthand, so the four sides
        // are written out; `border` itself it does expand, hence the resting rule
        // above can stay short.
        ':hover': {
            borderTopColor: tokens.colorBrandStroke2,
            borderRightColor: tokens.colorBrandStroke2,
            borderBottomColor: tokens.colorBrandStroke2,
            borderLeftColor: tokens.colorBrandStroke2,
            transform: 'translateY(-1px)',
            boxShadow: tokens.shadow4Brand,
        },
        '@media (prefers-reduced-motion: reduce)': {
            transitionDuration: '0.01ms',
            ':hover': { transform: 'none' },
        },
    },
    /** `.nx-card-h` — title row, no bottom padding; the body supplies it. */
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.spacingHorizontalS,
        padding: '14px 16px 0',
    },
    /** `.nx-card-h h3`. */
    cardTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        margin: 0,
        fontSize: '0.92rem',
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase300,
        color: tokens.colorNeutralForeground1,
    },
    /** `.nx-card-b`. */
    cardBody: {
        padding: '12px 16px 16px',
    },
    /* ── KPI tiles ────────────────────────────────────────────────────────── */
    /** `.nx-kpi-row` — the auto-fitting summary strip. */
    kpiGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: tokens.spacingHorizontalM,
        marginBottom: tokens.spacingVerticalXL,
    },
    /** `.nx-kpi` — a card that also lifts, with its own tighter padding. */
    kpi: {
        position: 'relative',
        boxSizing: 'border-box',
        overflow: 'hidden',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusMedium,
        border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
        boxShadow: tokens.shadow4,
        padding: '14px 16px',
        transitionProperty: 'border-color, transform, box-shadow',
        transitionDuration: tokens.durationNormal,
        transitionTimingFunction: tokens.curveEasyEase,
        // Griffel does not expand the `border-color` shorthand, so the four sides
        // are written out; `border` itself it does expand, hence the resting rule
        // above can stay short.
        ':hover': {
            borderTopColor: tokens.colorBrandStroke2,
            borderRightColor: tokens.colorBrandStroke2,
            borderBottomColor: tokens.colorBrandStroke2,
            borderLeftColor: tokens.colorBrandStroke2,
            transform: 'translateY(-1px)',
            boxShadow: tokens.shadow4Brand,
        },
        '@media (prefers-reduced-motion: reduce)': {
            transitionDuration: '0.01ms',
            ':hover': { transform: 'none' },
        },
    },
    /** `.nx-kpi-lbl`. */
    kpiLabel: {
        display: 'block',
        fontSize: '0.7rem',
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase200,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: tokens.colorNeutralForeground3,
    },
    /** `.nx-kpi-val` — the number the tile exists for. */
    kpiValue: {
        display: 'block',
        marginTop: '4px',
        fontSize: '1.7rem',
        fontWeight: tokens.fontWeightBold,
        lineHeight: 1,
        letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
        color: tokens.colorNeutralForeground1,
    },
    /** `.nx-kpi-sub`. */
    kpiSub: {
        display: 'block',
        marginTop: '2px',
        fontSize: '0.68rem',
        lineHeight: tokens.lineHeightBase200,
        color: tokens.colorNeutralForeground3,
    },
    /** `.nx-kpi-delta` — pair with one of the three direction classes. */
    kpiDelta: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXXS,
        marginTop: '6px',
        fontSize: '0.72rem',
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase200,
    },
    kpiDeltaUp: { color: tokens.colorPaletteGreenForeground1 },
    kpiDeltaDown: { color: tokens.colorPaletteRedForeground1 },
    kpiDeltaFlat: { color: tokens.colorNeutralForeground3 },
    /* ── Chips ────────────────────────────────────────────────────────────── */
    /**
     * `.nx-chip`. Nexora tints the status colour to 12%; Fluent has no alpha
     * function in tokens, but it *does* ship a validated tint pair per status —
     * exactly the pair `Badge appearance="tint"` uses. Reusing it means the chips
     * match any real `Badge` on the same page and stay legible in dark mode,
     * where a 12% tint of the dark-mode brand is a different colour entirely.
     */
    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXXS,
        whiteSpace: 'nowrap',
        padding: '2.5px 8px',
        borderRadius: tokens.borderRadiusMedium,
        fontSize: '0.68rem',
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase200,
    },
    chipGood: {
        color: tokens.colorPaletteGreenForeground1,
        backgroundColor: tokens.colorPaletteGreenBackground1,
    },
    chipWarn: {
        color: tokens.colorPaletteYellowForeground1,
        backgroundColor: tokens.colorPaletteYellowBackground1,
    },
    chipBad: {
        color: tokens.colorPaletteRedForeground1,
        backgroundColor: tokens.colorPaletteRedBackground1,
    },
    chipBrand: {
        color: tokens.colorBrandForeground2,
        backgroundColor: tokens.colorBrandBackground2,
    },
    chipNeutral: {
        color: tokens.colorNeutralForeground3,
        backgroundColor: tokens.colorNeutralBackground3,
    },
    /* ── Section header ───────────────────────────────────────────────────── */
    /** `.nx-sec` — icon tile, then a title over a caption. */
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalSNudge,
        marginBottom: tokens.spacingVerticalL,
    },
    /** `.nx-sec .ic` — the 30px brand-tinted square. */
    sectionIcon: {
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        width: '30px',
        height: '30px',
        borderRadius: tokens.borderRadiusLarge,
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground2,
    },
    sectionTitle: {
        display: 'block',
        margin: 0,
        fontSize: '0.95rem',
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase300,
        color: tokens.colorNeutralForeground1,
    },
    sectionSubtitle: {
        display: 'block',
        fontSize: '0.72rem',
        lineHeight: tokens.lineHeightBase200,
        color: tokens.colorNeutralForeground3,
    },
    /** `.nx-avatar` — brand-tinted initials disc, for a non-photo identity. */
    avatarTint: {
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        width: '36px',
        height: '36px',
        borderRadius: tokens.borderRadiusCircular,
        fontSize: '0.74rem',
        fontWeight: tokens.fontWeightSemibold,
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground2,
    },
    /* ── Tables ───────────────────────────────────────────────────────────── */
    /**
     * `.nx-table`, applied to Fluent's `Table` root.
     *
     * Fluent draws the row separator as a `border-bottom` on the row; Nexora draws
     * it as a `border-top` on the cell, which is what puts the line *under* the
     * sticky header rather than under the last row. Fluent's is suppressed here so
     * the two don't stack into a 2px rule. Row hover is left to Fluent
     * (`colorSubtleBackgroundHover` is already Nexora's "muted at 50%").
     */
    table: {
        width: '100%',
        '& .fui-TableRow': {
            borderBottomStyle: 'none',
        },
    },
    /** `.nx-table thead th`. */
    tableHead: {
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground3,
        padding: '0 16px',
        fontSize: '0.66rem',
        fontWeight: tokens.fontWeightSemibold,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        // The label lives in an inner button (Fluent puts the sort affordance
        // there), which resets its own font — so the header type has to be handed
        // down explicitly.
        '& .fui-TableHeaderCell__button': {
            minHeight: '34px',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            letterSpacing: 'inherit',
            textTransform: 'inherit',
            color: 'inherit',
        },
    },
    /** `.nx-table tbody td`. */
    tableCell: {
        padding: '12px 16px',
        fontSize: '0.82rem',
        lineHeight: tokens.lineHeightBase300,
        color: tokens.colorNeutralForeground1,
        borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    },
    /** `.nx-td-mono` — figures that must line up column-wise. */
    numeric: {
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
    },
    /* ── Empty state ──────────────────────────────────────────────────────── */
    /** `.nx-empty`. */
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: tokens.spacingVerticalS,
        padding: '48px 20px',
        color: tokens.colorNeutralForeground3,
    },
    /** `.nx-empty .ic` — 46px muted tile. */
    emptyIcon: {
        display: 'grid',
        placeItems: 'center',
        width: '46px',
        height: '46px',
        borderRadius: tokens.borderRadiusXLarge,
        marginBottom: tokens.spacingVerticalXXS,
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground3,
    },
    emptyTitle: {
        fontSize: '0.9rem',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    emptyText: {
        maxWidth: '320px',
        fontSize: '0.78rem',
        lineHeight: tokens.lineHeightBase300,
    },
    /* ── Meter ────────────────────────────────────────────────────────────── */
    /** `.nx-meter` — the 6px track. Pair with `meterFill` on the inner element. */
    meter: {
        height: '6px',
        borderRadius: tokens.borderRadiusSmall,
        backgroundColor: tokens.colorNeutralBackground3,
        overflow: 'hidden',
    },
    /** `.nx-meter i` — width is the only thing a caller sets inline. */
    meterFill: {
        display: 'block',
        height: '100%',
        borderRadius: tokens.borderRadiusSmall,
        backgroundColor: tokens.colorBrandBackground,
    },
    /* ── Entry animation ──────────────────────────────────────────────────── */
    /**
     * `.nx-fade` — content settles in rather than snapping. Suppressed entirely
     * under `prefers-reduced-motion`, since a translate is exactly the kind of
     * movement that setting exists to stop.
     */
    fadeIn: {
        animationName: {
            from: { opacity: 0, transform: 'translateY(4px)' },
            to: { opacity: 1, transform: 'none' },
        },
        animationDuration: '0.22s',
        animationTimingFunction: 'ease-out',
        animationFillMode: 'both',
        '@media (prefers-reduced-motion: reduce)': {
            animationName: 'none',
            animationDuration: '0.01ms',
        },
    },
});
//# sourceMappingURL=nexora.js.map